import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  approveCampaign,
  listApprovals,
  cancelCampaign,
  createCampaign,
  pauseCampaign,
  rejectCampaign,
  requestApproval,
  resumeCampaign,
  scheduleCampaign,
  setAudience,
  setSender,
  unscheduleCampaign,
} from '@/lib/campaigns-api';
import { keys } from '@/hooks/use-campaigns';

/**
 * The campaign lifecycle, as mutations (spec § 4.1).
 *
 * Separate from `use-campaigns.ts`, which owns reading and content editing. These are the state
 * TRANSITIONS — create, configure, approve, schedule, stop — and every one of them existed on the API
 * with nothing on the client able to call it, which is why no state between Draft and Sent was
 * reachable from the interface.
 *
 * ── EVERY ONE INVALIDATES THE CHECKLIST ───────────────────────────────────────────
 *
 * That is the rule to keep. The checklist is derived from the campaign, its audience, its sender, its
 * content and its brand's DNS, and it is the thing standing between a user and the send button. A
 * transition that changed any of those inputs and left a stale checklist on screen would show somebody
 * a green light computed before their last change.
 */

/** Invalidate everything downstream of a campaign changing. Cheap, and it cannot go stale by omission. */
function useInvalidateCampaign(campaignId: string) {
  const client = useQueryClient();

  return () => {
    void client.invalidateQueries({ queryKey: keys.campaign(campaignId) });
    void client.invalidateQueries({ queryKey: ['checklist', campaignId] });
    void client.invalidateQueries({ queryKey: ['report', campaignId] });
    void client.invalidateQueries({ queryKey: ['approvals', campaignId] });
    // The list shows status and audience size, so it is downstream too.
    void client.invalidateQueries({ queryKey: ['campaigns'] });
  };
}

/**
 * The approval history.
 *
 * `staleTime: 0`, like the checklist: an approval can land while somebody is looking at the screen,
 * and a cached "waiting on Sam" beside an enabled send button is the wrong pair of facts.
 */
export function useApprovals(campaignId: string) {
  return useQuery({
    queryKey: ['approvals', campaignId] as const,
    queryFn: () => listApprovals(campaignId),
    staleTime: 0,
  });
}

/**
 * Creates a campaign and navigates into it.
 *
 * The navigation is part of the mutation rather than the caller's job because there is exactly one
 * sensible thing to do after creating a campaign — start writing it — and leaving the user on the list
 * to find the row they just made is the kind of gap that makes an app feel unfinished.
 */
export function useCreateCampaign() {
  const client = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (body: { brandId: string; name: string; subject: string }) => createCampaign(body),

    onSuccess: (campaign) => {
      void client.invalidateQueries({ queryKey: ['campaigns'] });
      void navigate(`/campaigns/${campaign.id}`);
    },
  });
}

export function useSetAudience(campaignId: string) {
  const invalidate = useInvalidateCampaign(campaignId);

  return useMutation({
    mutationFn: (body: { segmentId: string; excludeSegmentId: string | null; topicId: string | null }) =>
      setAudience(campaignId, body),
    onSuccess: invalidate,
  });
}

export function useSetSender(campaignId: string) {
  const invalidate = useInvalidateCampaign(campaignId);

  return useMutation({
    mutationFn: (senderIdentityId: string) => setSender(campaignId, senderIdentityId),
    onSuccess: invalidate,
  });
}

export function useRequestApproval(campaignId: string) {
  const invalidate = useInvalidateCampaign(campaignId);

  return useMutation({
    mutationFn: (body: { approverSubject: string; approverEmail: string; comment: string | null }) =>
      requestApproval(campaignId, body),
    onSuccess: invalidate,
  });
}

export function useApprove(campaignId: string) {
  const invalidate = useInvalidateCampaign(campaignId);

  return useMutation({
    mutationFn: (comment: string | null) => approveCampaign(campaignId, comment),
    onSuccess: invalidate,
  });
}

export function useReject(campaignId: string) {
  const invalidate = useInvalidateCampaign(campaignId);

  return useMutation({
    mutationFn: (comment: string | null) => rejectCampaign(campaignId, comment),
    onSuccess: invalidate,
  });
}

export function useSchedule(campaignId: string) {
  const invalidate = useInvalidateCampaign(campaignId);

  return useMutation({
    mutationFn: (at: string) => scheduleCampaign(campaignId, at),
    onSuccess: invalidate,
  });
}

export function useUnschedule(campaignId: string) {
  const invalidate = useInvalidateCampaign(campaignId);

  return useMutation({
    mutationFn: () => unscheduleCampaign(campaignId),
    onSuccess: invalidate,
  });
}

/**
 * Pause, resume and cancel.
 *
 * Deliberately NOT optimistic, unlike a toggle. Each returns a count of what was actually stopped
 * against what is already past recall, and that number is the answer the user needs — an optimistic
 * "paused" that later turns out to have stopped nothing is worse than a second of waiting.
 */
export function usePause(campaignId: string) {
  const invalidate = useInvalidateCampaign(campaignId);

  return useMutation({
    mutationFn: (reason: string) => pauseCampaign(campaignId, reason),
    onSuccess: invalidate,
  });
}

export function useResume(campaignId: string) {
  const invalidate = useInvalidateCampaign(campaignId);

  return useMutation({
    mutationFn: () => resumeCampaign(campaignId),
    onSuccess: invalidate,
  });
}

export function useCancel(campaignId: string) {
  const invalidate = useInvalidateCampaign(campaignId);

  return useMutation({
    mutationFn: (reason: string) => cancelCampaign(campaignId, reason),
    onSuccess: invalidate,
  });
}
